import { computed, shallowRef } from "vue";
import { defineStore } from "pinia";
import { toMaybe } from "../lib/maybe";
import {
  convertProjectDocuments,
  listLegacyDocuments,
} from "../lib/tauri/conversion";
import { planProjectConversion } from "../lib/zwdoc/convertProject";
import {
  initialProjectConversionModel,
  updateProjectConversion,
  type ProjectConversionMsg,
} from "./useProjectConversionStore.update";

/**
 * Converts a project's Markdown documents to `.zwdoc` when it opens
 * (#259): one commit, all or nothing. Runs again after a cloud pull, so
 * documents another device added before converting are picked up too.
 *
 * TEA: pure Model/Msg in `useProjectConversionStore.update.ts`; the effects
 * (list, plan, apply) are this store's `convertIfNeeded`.
 */
export const useProjectConversionStore = defineStore(
  "projectConversion",
  () => {
    const model = shallowRef(initialProjectConversionModel);

    function dispatch(msg: ProjectConversionMsg): void {
      model.value = updateProjectConversion(model.value, msg)[0];
    }

    /**
     * True when documents were converted (the caller reloads the payload).
     * False when there was nothing to do, or conversion failed (see `model`).
     */
    async function convertIfNeeded(rootPath: string): Promise<boolean> {
      const legacy = await listLegacyDocuments(rootPath);
      if (legacy.documents.length === 0) return false;
      dispatch({ type: "Started", documents: legacy.documents.length });
      const plan = planProjectConversion({
        documents: legacy.documents,
        existing: legacy.existing,
        zwconfig: toMaybe(legacy.zwconfig),
        zwgraph: toMaybe(legacy.zwgraph),
      });
      if (plan.ok === false) {
        dispatch({ type: "PlanRejected", failures: plan.error });
        return false;
      }
      try {
        await convertProjectDocuments(rootPath, plan.value);
      } catch (error) {
        dispatch({
          type: "ApplyFailed",
          error: error instanceof Error ? error.message : String(error),
        });
        return false;
      }
      dispatch({ type: "Converted" });
      return true;
    }

    function dismiss(): void {
      dispatch({ type: "Dismissed" });
    }

    return {
      model: computed(() => model.value),
      convertIfNeeded,
      dismiss,
    };
  },
);
